// for interns 
"use client"

import { useState, useEffect, useRef } from "react"
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  useTheme,
  Stack,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import SearchIcon from "@mui/icons-material/Search"
import useAuthStore from "../../Zustand/Store/useAuthStore"
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls"
import {
  resolveAttendancePageState,
  clearAttendanceLandingState,
  isManagerKioskPageState,
  getAttendanceOrganizationsForUser,
  getManagerAttendanceOrganizations,
  isManagerAttendanceMode,
  persistAttendanceLandingState,
  resolveKioskSoundEnabledAsync,
  resolveKioskSoundEnabled,
} from "../../Utils/attendanceAuth"
import {
  playAttendanceSuccessSoundIfEnabled,
  unlockAttendanceAudio,
} from "../../Utils/attendanceSound"
import { clearClientPreferredOrganizationId } from "../../Utils/clientActiveOrganization"
import Swal from "sweetalert2"
import axios from "axios"
import toast from "react-hot-toast"
import { useLocation, useNavigate } from "react-router-dom"
import { LogOutIcon } from "lucide-react"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import WorkTimeClock from "../../Components/WorkTimeClock/WorkTimeClock"
import { format } from "date-fns";





const StyledContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#ffffff",
  minHeight: "auto",
  padding: theme.spacing(2, 2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}))

const HeaderSection = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(2),
  color: "text.primary",
}))

const CircleContainer = styled(Box)(({ theme }) => ({
  width: 160,
  height: 160,
  border: "2px solid #4caf50",
  borderRadius: "50%",
  margin: theme.spacing(1.5, 0),
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#ffffff",
  color: "text.primary",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  overflow: "hidden",
  position: "relative",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#ffffff",
  },
}))

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.75),
  flexWrap: "wrap",
  justifyContent: "center",
  marginTop: theme.spacing(1.75),
}))

const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: 76,
  minHeight: 46,
  border: "1px solid #4caf50",
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#ffffff",
  color: "text.primary",
  fontSize: "11.5px",
  fontWeight: "normal",
  textTransform: "none",
  padding: theme.spacing(0.5, 0.75),
  "&:hover": {
    backgroundColor: "#f0f8f0",
  },
}))

const ChangePhotoButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: "10px",
  minHeight: "24px",
  padding: theme.spacing(0.5, 1),
  backgroundColor: "#4caf50",
  color: "white",
  textTransform: "none",
  borderRadius: theme.spacing(1),
  "&:hover": {
    backgroundColor: "#45a049",
  },
}))

const ImageContainer = styled(Box)({
  width: "100%",
  height: "100%",
  position: "relative",
  "&:hover .edit-overlay": {
    opacity: 1,
  },
})

const SearchContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 400,
  marginBottom: theme.spacing(2),
  position: "relative",
  color: "text.primary",
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#ffffff",
    borderRadius: theme.spacing(1),
    "& fieldset": {
      borderColor: "#4caf50",
    },
    "&:hover fieldset": {
      borderColor: "#45a049",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4caf50",
    },
  },
  "& .MuiInputLabel-root": {
    color: "text.primary",
    "&.Mui-focused": {
      color: "text.primary",
    },
  },
}))

const SearchResultsContainer = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 1000,
  maxHeight: 200,
  overflowY: "auto",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#ffffff",
  color: "text.primary",
  border: "1px solid #4caf50",
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(0.5),
}))

const SearchResultItem = styled(ListItem)(({ theme }) => ({
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#ffffff",
  },
  borderBottom: "1px solid #e0e0e0",
  "&:last-child": {
    borderBottom: "none",
  },
}))

const FaceAuth2 = () => {
  const location = useLocation()
  const { userData, resetAuth } = useAuthStore()
  const org = userData?.organization
  const item = resolveAttendancePageState(userData, location.state)
  const isKioskMode = isManagerKioskPageState(item, userData)
  const clientOrganizations = isManagerAttendanceMode(userData)
    ? getManagerAttendanceOrganizations(userData)
    : getAttendanceOrganizationsForUser(userData)
  const showChangeOrganization =
    isKioskMode && clientOrganizations.length > 1

  const attendanceOrganizationId =
    item?.organization_id ?? org?.organization_id ?? null

  const maybeUnlockAttendanceAudio = () => {
    if (resolveKioskSoundEnabled(userData, attendanceOrganizationId)) {
      unlockAttendanceAudio()
    }
  }

  useEffect(() => {
    if (!attendanceOrganizationId) return

    void resolveKioskSoundEnabledAsync(userData, attendanceOrganizationId)
  }, [userData, attendanceOrganizationId])

  const { logout } = useAuthStore()

  const organizationPersonIdFromContext = item?.organization_person_id || item?.intern_id

  const [hasPreselectedIntern, setHasPreselectedIntern] = useState(
    !isKioskMode &&
      Boolean(item?.organization_person_id || item?.intern_id)
  )
  const fetchAbortControllerRef = useRef(null)

  console.log("item is  ", item)
  console.log("  Current userData:", userData)
  console.log("  Organization data:", org)
  console.log("  Organization name:", item?.organization_name)

  const [currentDateTime, setCurrentDateTime] = useState("")
  const [isCapturing, setIsCapturing] = useState(false)
  const [isCameraActive, setIsCameraActive] = useState(false)
  const [faceCaptureStatus, setFaceCaptureStatus] = useState("")
  const [capturedImage, setCapturedImage] = useState(null)
  const [cameraError, setCameraError] = useState("")
  const [permissionState, setPermissionState] = useState("prompt")
  const navigate = useNavigate()
  const [userData2, setUserData2] = useState({
    name: "Name",
    functionalRole: "Functional Role",
    authorized: false,
    organization_person_id: null,
    intern_id: null, // Keep for backward compatibility
    profileImage: "Profile_Image_Url",
    currentTotal: null,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [organizationData, setOrganizationData] = useState({
    organization_id: item?.organization_id,
    name: item?.organization_name,
  })
  const [clockInTime, setClockInTime] = useState(null)
  const [isClockedIn, setIsClockedIn] = useState(false)
  const [breakStartTime, setBreakStartTime] = useState(null)
  const [isBreakStarted, setIsBreakStarted] = useState(false)
  const [breakEndTime, setBreakEndTime] = useState(null)
  const [isBreakEnded, setIsBreakEnded] = useState(false)
  const [clockOutTime, setClockOutTime] = useState(null)
  const [isClockedOut, setIsClockedOut] = useState(false)
  const [showAttendanceRecord, setShowAttendanceRecord] = useState(false)
  const [selectedIntern, setSelectedIntern] = useState(null)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")


  const [buttonsDisabled, setButtonsDisabled] = useState(false);




  const [allowed, setAllowed] = useState({
    clockin: true,
    breakstart: false,
    breakend: false,
    clockout: false,
  })

  const getLogTimeByType = (logs = [], logType) => {
    if (!Array.isArray(logs)) return null
    const matchedLog = logs
      .filter((entry) => entry?.attendance_log_type === logType)
      .sort(
        (a, b) =>
          new Date(b?.attendance_log_time || b?.created_at || 0).getTime() -
          new Date(a?.attendance_log_time || a?.created_at || 0).getTime()
      )[0]

    return matchedLog?.attendance_log_time || matchedLog?.created_at || null
  }

  const formatButtonTime = (timeValue) => {
    if (!timeValue) return null
    const parsed = new Date(timeValue)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed
      .toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toUpperCase()
  }

  const attendanceLogs = userData2?.TotalAttendance || userData2?.Today_Attendance || []
  const clockInDisplayTime = formatButtonTime(
    clockInTime || getLogTimeByType(attendanceLogs, "Clock In")
  )
  const breakStartDisplayTime = formatButtonTime(
    breakStartTime || getLogTimeByType(attendanceLogs, "Break Start")
  )
  const breakEndDisplayTime = formatButtonTime(
    breakEndTime || getLogTimeByType(attendanceLogs, "Break End")
  )
  const clockOutDisplayTime = formatButtonTime(
    clockOutTime || getLogTimeByType(attendanceLogs, "Clock Out")
  )

  const resetForNextIntern = () => {
    setUserData2({
      name: "Name",
      functionalRole: "Functional Role",
      authorized: false,
      organization_person_id: null,
      intern_id: null,
      profileImage: "Profile_Image_Url",
      currentTotal: null,
      TotalBreaks: null,
      TotalAttendance: null,
      Today_Attendance: null,
      Should_Stop_BreakCal: null,
      Stop_Cal_Work: null,
    })
    setHasPreselectedIntern(false)
    setSearchQuery("")
    setSearchResults([])
    setShowResults(false)
    setAllowed({
      clockin: true,
      breakstart: false,
      breakend: false,
      clockout: false,
    })
    setButtonsDisabled(false)
    setCameraError("")
    resetAttendanceStates()
  }

  const resetAttendanceStates = () => {
    setClockInTime(null)
    setBreakStartTime(null)
    setBreakEndTime(null)
    setClockOutTime(null)
    setShowAttendanceRecord(false)
    setSelectedIntern(null)
  }

  const completeAttendanceAction = async (message) => {
    const soundEnabled = await resolveKioskSoundEnabledAsync(
      userData,
      attendanceOrganizationId
    )
    playAttendanceSuccessSoundIfEnabled(soundEnabled)

    setToastMessage(message)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)

    resetAttendanceStates()
    window.location.reload()
  }

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token")

          await axios.get(`${MAIN_URL}/api/auth/logout`, {
            headers: getApiHeaders(),
          })
          logout()
          clearClientPreferredOrganizationId()
          clearAttendanceLandingState()
          localStorage.removeItem("token")
          Swal.fire("Logged out!", "You have been logged out.", "success")
          resetAuth()
          navigate("/login")
        } catch (error) {
          console.error("Logout failed:", error)
          logout()
          clearClientPreferredOrganizationId()
          clearAttendanceLandingState()
          localStorage.removeItem("token")
          navigate("/login")
        }
      }
    })
  }

  const handleBack = () => {
    try {
      if (showChangeOrganization) {
        clearAttendanceLandingState()
        navigate("/organization-toggle", { replace: true })
        return
      }
      navigate(-1)
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return
      }

      console.error("Navigation failed:", error)
    }
  }

  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const videoRef = useRef(null)

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setIsCameraActive(false)
    setIsCapturing(false)
  }

  useEffect(() => {
    if (isCameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current
      videoRef.current.play().catch(() => {})
    }
  }, [isCameraActive])

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [])

  const theme = useTheme()

  const [apiUrl, setApiUrl] = useState(`${MAIN_URL}/api/organizations/${item?.organization_id}/filter-intern`)

  const checkCameraPermission = async () => {
    try {
      const result = await navigator.permissions.query({ name: "camera" })
      setPermissionState(result.state)
      return result.state
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return
      }

      console.log("  Permission API not supported, proceeding with getUserMedia")
      return "prompt"
    }
  }

  const captureImageDirectly = async () => {
    try {
      console.log("  Starting direct image capture...")
      setCameraError("")
      setIsCapturing(true)
      setIsLoading(true)

      const permissionStateResult = await checkCameraPermission()
      console.log("  Camera permission state:", permissionStateResult)

      if (permissionStateResult === "denied") {
        setCameraError("Camera access denied. Please enable camera permissions in your browser settings.")
        setIsCapturing(false)
        setIsLoading(false)
        return
      }

      console.log("  Requesting camera access...")
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 320 },
          height: { ideal: 240 },
          facingMode: "user",
        },
      })

      console.log("  Camera access granted, capturing image...")
      streamRef.current = stream

      const video = document.createElement("video")
      video.srcObject = stream
      video.play()

      video.onloadedmetadata = () => {
        const canvas = canvasRef.current
        if (canvas) {
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          const context = canvas.getContext("2d")
          context.drawImage(video, 0, 0)

          const imageDataUrl = canvas.toDataURL("image/jpeg", 0.8)
          setCapturedImage(imageDataUrl)

          canvas.toBlob(
            async (blob) => {
              if (blob) {
                if (isKioskMode) {
                  await performFaceIdentification(blob)
                } else {
                  await performFaceRecognition(blob)
                }
              }
            },
            "image/jpeg",
            0.8,
          )
        }

        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }

      setPermissionState("granted")
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return
      }

      console.error("  Error accessing camera:", error)

      let errorMessage = ""
      if (error.name === "NotAllowedError") {
        errorMessage = "Camera access denied. Please allow camera permissions and try again."
      } else if (error.name === "NotFoundError") {
        errorMessage = "No camera found. Please ensure your device has a camera connected."
      } else {
        errorMessage = `Camera error: ${error.message || "Unknown error occurred"}`
      }

      setCameraError(errorMessage)
      setIsCapturing(false)
      setIsLoading(false)
      setPermissionState("denied")
    }
  }

  const performFaceIdentification = async (imageBlob) => {
    try {
      const organizationId = item?.organization_id || org?.organization_id
      if (!organizationId) {
        throw new Error("Organization context is missing.")
      }

      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Authentication token is missing. Please login again.")
      }

      const formData = new FormData()
      formData.append("image", imageBlob, "face.jpg")
      formData.append("organization_id", organizationId.toString())
      formData.append("person_type", "intern")

      const response = await fetch(`${MAIN_URL}/api/face/identify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result.success || !result.match) {
        throw new Error(result.message || "Face not recognized. Please try again.")
      }

      await loadInternAfterIdentify(result)
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return
      }

      setCameraError(error.message || "Face identification failed. Please try again.")
      setUserData2((prev) => ({ ...prev, authorized: false }))
    } finally {
      setIsLoading(false)
      setIsCapturing(false)
    }
  }

  const loadInternAfterIdentify = async (identifyResult) => {
    const token = localStorage.getItem("token")
    const organizationId = item?.organization_id || org?.organization_id

    const intern = await fetchFilterInternStatus({
      MAIN_URL,
      organizationId,
      token,
      organizationPersonId: identifyResult.organization_person_id,
      internId: identifyResult.intern_id,
    })

    if (intern) {
      handleInternSelect(intern)
      return
    }

    setUserData2({
      name: identifyResult.name || "Name",
      functionalRole: identifyResult.functional_role || "Functional Role",
      authorized: true,
      organization_person_id: identifyResult.organization_person_id,
      intern_id: identifyResult.intern_id,
      profileImage: identifyResult.profile_image_url || "Profile_Image_Url",
      currentTotal: null,
      TotalBreaks: null,
      TotalAttendance: null,
      Today_Attendance: null,
      Should_Stop_BreakCal: null,
      Stop_Cal_Work: null,
    })
    setHasPreselectedIntern(true)
    setAllowed({
      clockin: true,
      breakstart: false,
      breakend: false,
      clockout: false,
    })
  }

  const performFaceRecognition = async (imageBlob, { manageLoadingState = true } = {}) => {
    try {
      const organizationId = item?.organization_id || org?.organization_id
      const organizationPersonId =
        userData2?.organization_person_id ||
        item?.organization_person_id ||
        organizationPersonIdFromContext

      let internRecordId = userData2?.intern_id || item?.intern_id || null

      if (!organizationId) {
        throw new Error("Organization context is missing.")
      }

      if (!organizationPersonId) {
        throw new Error("Intern record is missing. Please login again.")
      }

      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Authentication token is missing. Please login again.")
      }

      if (
        !internRecordId ||
        String(internRecordId) === String(organizationPersonId)
      ) {
        const intern = await fetchFilterInternStatus({
          MAIN_URL,
          organizationId,
          token,
          organizationPersonId,
          internId: userData2?.intern_id || item?.intern_id,
        })
        internRecordId = intern?.intern_id || intern?.Intern_ID || internRecordId
      }

      if (!internRecordId) {
        throw new Error("Intern ID is missing. Please login again.")
      }

      const formData = new FormData()
      formData.append("image", imageBlob, "face.jpg")
      formData.append("organization_id", organizationId.toString())
      formData.append("employee_id", String(internRecordId))
      formData.append("organization_person_id", String(organizationPersonId))

      const response = await fetch(`${MAIN_URL}/api/face/recognize`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result.success || !result.match) {
        throw new Error(result.message || "Face not recognized. Please try again.")
      }

      setUserData2((prev) => ({
        ...prev,
        authorized: true,
        name: result.name || prev.name,
        functionalRole: result.functional_role || prev.functionalRole,
        intern_id: result.intern_id || internRecordId || prev.intern_id,
        organization_person_id:
          result.organization_person_id ||
          result.intern?.organization_person_id ||
          organizationPersonId ||
          prev.organization_person_id,
        profileImage:
          result.profile_image_url ||
          result.intern?.profile_image_url ||
          prev.profileImage,
        currentTotal: prev.currentTotal,
        TotalAttendance: prev.TotalAttendance,
        TotalBreaks: prev.TotalBreaks,
        Should_Stop_BreakCal: prev.Should_Stop_BreakCal,
        Stop_Cal_Work: prev.Stop_Cal_Work,
      }))
      setCameraError("")
      return true
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return false
      }

      console.error("Face recognition failed:", error)
      setUserData2((prev) => ({ ...prev, authorized: false }))
      setCameraError(error.message || "Face recognition failed. Please try again.")
      return false
    } finally {
      if (manageLoadingState) {
        setIsLoading(false)
        setIsCapturing(false)
      }
    }
  }

  const captureSingleFrameBlob = async () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) {
      throw new Error("Camera not ready")
    }

    canvas.width = video.videoWidth || 320
    canvas.height = video.videoHeight || 240
    const context = canvas.getContext("2d")
    context.drawImage(video, 0, 0)

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result)
          else reject(new Error("Failed to capture image"))
        },
        "image/jpeg",
        0.85
      )
    })
  }

  const startFaceVerificationAndAction = async (actionFn) => {
    if (isLoading || isCameraActive) return

    maybeUnlockAttendanceAudio()

    if (userData2.name === "Name" || userData2.functionalRole === "Functional Role") {
      alert("Please select an intern first")
      return
    }

    if (isKioskMode) {
      setButtonsDisabled(true)
      try {
        await actionFn()
      } finally {
        setButtonsDisabled(false)
      }
      return
    }

    setButtonsDisabled(true)
    setIsLoading(true)
    setIsCapturing(true)
    setCameraError("")
    setFaceCaptureStatus("opening")

    try {
      const permissionStateResult = await checkCameraPermission()
      if (permissionStateResult === "denied") {
        setCameraError(
          "Camera access denied. Please enable camera permissions in your browser settings."
        )
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 480 },
          height: { ideal: 480 },
          facingMode: "user",
        },
      })

      streamRef.current = stream
      setIsCameraActive(true)
      setFaceCaptureStatus("capturing")
      setPermissionState("granted")

      await new Promise((resolve) => setTimeout(resolve, 1200))
      await new Promise((resolve) => setTimeout(resolve, 800))

      const blob = await captureSingleFrameBlob()
      stopCamera()
      setFaceCaptureStatus("verifying")

      const matched = await performFaceRecognition(blob, {
        manageLoadingState: false,
      })
      if (!matched) {
        setFaceCaptureStatus("failed")
        return
      }

      setFaceCaptureStatus("verified")
      await actionFn()
    } catch (error) {
      stopCamera()
      setFaceCaptureStatus("failed")

      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return
      }

      let errorMessage =
        error.message || "Face verification failed. Please try again."
      if (error.name === "NotAllowedError") {
        errorMessage =
          "Camera access denied. Please allow camera permissions and try again."
        setPermissionState("denied")
      } else if (error.name === "NotFoundError") {
        errorMessage =
          "No camera found. Please ensure your device has a camera connected."
      }

      setCameraError(errorMessage)
    } finally {
      setIsLoading(false)
      setIsCapturing(false)
      setButtonsDisabled(false)
      setTimeout(() => setFaceCaptureStatus(""), 2500)
    }
  }

  const handleChangePhoto = (e) => {
    e.stopPropagation()
    console.log("  Changing photo - resetting captured image")
    setCapturedImage(null)
    setCameraError("")
    setUserData2((prev) => ({ ...prev, authorized: false }))
  }

  const handleCameraClick = () => {
    if (isKioskMode) {
      if (userData2.name !== "Name" || isLoading || isCapturing) return
      captureImageDirectly()
      return
    }

    if (isLoading || isCapturing) return
    captureImageDirectly()
  }

  const searchInterns = async (query) => {
    if (!query.trim()) {
      setSearchResults([])
      setShowResults(false)
      return
    }

    if (!apiUrl) {
      console.error("  API URL not available yet")
      return
    }

    setIsSearching(true)
    try {
      const token = localStorage.getItem("token")
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        mode: "cors",
      })

      console.log("  Search response status:", response.status)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`)
      }

      const data = await response.json()
      console.log("  Raw search API response:", data)

      let interns = Array.isArray(data) ? data : data.interns || data.data || []

      if (Array.isArray(interns) && interns.length > 0 && Array.isArray(interns[0])) {
        interns = interns[0]
      }

      console.log("  Total interns from API:", interns.length)

      const filteredInterns = interns.filter((intern) => {
        const internName = (intern.Name || "").toLowerCase()
        const functionalRole = (intern.Functional_Role || "").toLowerCase()

        const internCode = (
          intern.Intern_Code ||
          intern.intern_code ||
          intern.internCode ||
          intern.code ||
          ""
        )
          .toString()
          .toLowerCase()
        const searchTerm = query.toLowerCase().trim()

        console.log("  Checking intern:", {
          id: intern.id || intern.intern_id,
          name: internName,
          functionalRole: functionalRole,
          internCode: internCode,
          originalInternCode: intern.Intern_Code,
          searchTerm: searchTerm,
        })

        const nameMatch = internName.includes(searchTerm)
        const roleMatch = functionalRole.includes(searchTerm)
        const codeMatch = internCode.includes(searchTerm)

        const isMatch = nameMatch || roleMatch || codeMatch

        if (isMatch) {
          console.log("  MATCH FOUND:", {
            intern: intern.Name || intern.name,
            code: intern.Intern_Code,
            matchType: nameMatch ? "name" : roleMatch ? "functionalRole" : "code",
          })
        }

        return isMatch
      })

      console.log("  Filtered interns count:", filteredInterns.length)
      console.log("  Search query was:", query)

      setSearchResults(filteredInterns)
      setShowResults(true)
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return
      }

      console.error("  Detailed search error:", error)
      setSearchResults([])
      setShowResults(true)
    } finally {
      setIsSearching(false)
    }
  }

  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearchQuery(value)

    const timeoutId = setTimeout(() => {
      searchInterns(value)
    }, 300)

    if (handleSearchChange.timeoutId) {
      clearTimeout(handleSearchChange.timeoutId)
    }
    handleSearchChange.timeoutId = timeoutId
  }

  const handleInternSelect = (intern) => {
    const internName = intern.Name || intern.name
    const internFunctionalRole = intern.Functional_Role || intern.functionalRole || "Intern"
    // The 'id' field IS the organization_person_id in the API response
    const organizationPersonId = intern.id || intern.organization_person_id
    const internId = intern.intern_id || intern.Intern_ID // Keep for backward compatibility
    const profileImageUrl = intern.profile_image_url || intern.Profile_Image_Url || intern.profileImage
    const currentTotal = intern.Total_Worked_Time
    const TotalAttendance = intern?.Today_Attendance
    const TotalBreaks = intern?.Total_Breaks
    const Should_Stop_BreakCal = intern?.Should_Stop_BreakCal
    const Stop_Cal_Work = intern?.Stop_Cal_Work

    console.log("Selected intern:", intern)
    console.log("Extracted name:", internName)
    console.log("Extracted functionalRole:", internFunctionalRole)
    console.log("Extracted organization_person_id:", organizationPersonId)
    console.log("Extracted profile image:", profileImageUrl)

    setUserData2({
      name: internName,
      functionalRole: internFunctionalRole,
      authorized: false,
      organization_person_id: organizationPersonId,
      intern_id: internId, // Keep for backward compatibility
      profileImage: profileImageUrl,
      currentTotal: currentTotal,
      TotalAttendance: TotalAttendance,
      TotalBreaks: TotalBreaks,
      Should_Stop_BreakCal: Should_Stop_BreakCal,
      Stop_Cal_Work: Stop_Cal_Work,
    })
    setSearchQuery(internName)
    setShowResults(false)
    setSearchResults([])

    setAllowed({
      clockin: Boolean(intern.clockin ?? intern.allowed?.clockin ?? true),
      breakstart: Boolean(intern.breakstart ?? intern.allowed?.breakstart ?? false),
      breakend: Boolean(intern.breakend ?? intern.allowed?.breakend ?? false),
      clockout: Boolean(intern.clockout ?? intern.allowed?.clockout ?? false),
    })
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".search-container")) {
        setShowResults(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const refreshInternStatus = async () => {
    try {
      if (!item?.organization_id) return
      if (!userData2?.organization_person_id && !userData2?.intern_id) return

      const token = localStorage.getItem("token")
      if (!token) return

      // Only use organization_person_id or intern_id, not name to avoid matching wrong intern
      const intern = await fetchFilterInternStatus({
        MAIN_URL,
        organizationId: item?.organization_id,
        token,
        organizationPersonId: userData2.organization_person_id || userData2.intern_id,
        internId: userData2.intern_id,
      })

      // Only update status if we got a valid intern and it matches the current user
      // Filter API returns id (same as organization_person_id); use id || organization_person_id
      const internOrgPersonId = intern.id || intern.organization_person_id;
      const internIdVal = intern.intern_id || intern.Intern_ID || intern.id;
      if (intern && (
        internOrgPersonId === userData2.organization_person_id ||
        internOrgPersonId === userData2.intern_id ||
        internIdVal === userData2.intern_id ||
        internIdVal === userData2.organization_person_id
      )) {
        setIsClockedIn(Boolean(intern?.clockin))
        setIsBreakStarted(Boolean(intern?.breakstart))
        setIsBreakEnded(Boolean(intern?.breakend))
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return
      }

      console.log("[v0] refreshInternStatus error:", err)
    }
  }

  useEffect(() => {
    // Only refresh if we have a valid organization_person_id or intern_id
    // Don't trigger on name change to avoid race conditions
    if (userData2.organization_person_id || userData2.intern_id) {
      refreshInternStatus()
    }
  }, [userData2.organization_person_id, userData2.intern_id, item?.organization_id])

  const executeClockIn = async () => {
    const organizationPersonId = userData2.organization_person_id || userData2.intern_id;
    if (!organizationPersonId) {
      alert("Organization Person ID is missing. Please select an intern from the search results.")
      return
    }

    const token = localStorage.getItem("token")
    if (!token) {
      alert("Authentication token is missing. Please login again.")
      return
    }

    const now = new Date()
    const timestamp = now.toISOString()
    const formData = new FormData()
    formData.append("organization_person_id", organizationPersonId.toString())
    formData.append("attendance_log_type", "Clock In")
    formData.append("timestamp", timestamp)
    formData.append("date", now.toISOString().split("T")[0])
    formData.append("remarks", ` Created By ${userData?.full_name} --  Application user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')})`)
    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/intern-time-logs`
    const response = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.ok) {
      await response.json().catch(() => null)

      setClockInTime(timestamp)
      setIsClockedIn(true)
      setSelectedIntern(userData2)

      setAllowed({
        clockin: false,
        breakstart: true,
        breakend: false,
        clockout: true,
      })
      await refreshInternStatus()

      await completeAttendanceAction("Intern Login Successfully")
    } else {
      const text = await response.text().catch(() => "")
      console.error("Clock in failed:", response.status, text)
      alert("Clock in failed. Please try again.")
    }
  }

  const handleClockIn = async () => {
    try {
      await startFaceVerificationAndAction(executeClockIn)
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!")
        navigate("/login")
        return
      }
      console.error("Error during Clock In:", error)
      alert("An error occurred while clocking in. Please try again.")
    }
  }

  const executeStartBreak = async () => {
    const organizationPersonId = userData2?.organization_person_id || userData2?.intern_id;
    if (!organizationPersonId) {
      alert("Please select an intern first")
      return
    }
    const token = localStorage.getItem("token")
    if (!token) {
      alert("Authentication token is missing. Please login again.")
      return
    }

    const now = new Date()
    const timestamp = now.toISOString()
    const formData = new FormData()
    formData.append("organization_person_id", String(organizationPersonId))
    formData.append("attendance_log_type", "Break Start")
    formData.append("timestamp", timestamp)
    formData.append("date", now.toISOString().split("T")[0])
    formData.append("remarks", ` Created By ${userData?.full_name} --   user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')})`)

    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/intern-time-logs`
    const res = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: { Authorization: `Bearer ${token}` },
    })

    if (!res.ok) {
      const t = await res.text().catch(() => "")
      throw new Error(`Break start failed: ${res.status} ${t}`)
    }

    setIsBreakStarted(true)
    setBreakStartTime(timestamp)

    setAllowed({
      clockin: false,
      breakstart: false,
      breakend: true,
      clockout: true,
    })
    await refreshInternStatus()

    await completeAttendanceAction("Break Started Successfully")
  }

  const handleStartBreak = async () => {
    try {
      await startFaceVerificationAndAction(executeStartBreak)
    } catch (err) {
      console.error(err)
      alert("Failed to start break.")
    }
  }

  const executeEndBreak = async () => {
    const organizationPersonId = userData2?.organization_person_id || userData2?.intern_id;
    if (!organizationPersonId) {
      alert("Please select an intern first")
      return
    }
    const token = localStorage.getItem("token")
    if (!token) {
      alert("Authentication token is missing. Please login again.")
      return
    }

    const now = new Date()
    const timestamp = now.toISOString()
    const formData = new FormData()
    formData.append("organization_person_id", String(organizationPersonId))
    formData.append("attendance_log_type", "Break End")
    formData.append("timestamp", timestamp)
    formData.append("date", now.toISOString().split("T")[0])
    formData.append("remarks", ` Created By ${userData?.full_name} --   user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')})`)

    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/intern-time-logs`
    const res = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: { Authorization: `Bearer ${token}` },
    })

    if (!res.ok) {
      const t = await res.text().catch(() => "")
      throw new Error(`Break end failed: ${res.status} ${t}`)
    }

    setIsBreakEnded(true)
    setBreakEndTime(timestamp)

    setAllowed({
      clockin: false,
      breakstart: true,
      breakend: false,
      clockout: true,
    })
    await refreshInternStatus()

    await completeAttendanceAction("Break Ended Successfully")
  }

  const handleEndBreak = async () => {
    try {
      await startFaceVerificationAndAction(executeEndBreak)
    } catch (err) {
      console.error(err)
      alert("Failed to end break.")
    }
  }

  const executeClockOut = async () => {
    const organizationPersonId = userData2?.organization_person_id || userData2?.intern_id;
    if (!organizationPersonId) {
      alert("Please select an intern first")
      return
    }
    const token = localStorage.getItem("token")
    if (!token) {
      alert("Authentication token is missing. Please login again.")
      return
    }

    const now = new Date()
    const timestamp = now.toISOString()
    const formData = new FormData()
    formData.append("organization_person_id", String(organizationPersonId))
    formData.append("attendance_log_type", "Clock Out")
    formData.append("timestamp", timestamp)
    formData.append("date", now.toISOString().split("T")[0])
    formData.append("remarks", ` Created By ${userData?.full_name} --   user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')})`)

    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/intern-time-logs`
    const res = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: { Authorization: `Bearer ${token}` },
    })

    if (!res.ok) {
      const t = await res.text().catch(() => "")
      throw new Error(`Clock out failed: ${res.status} ${t}`)
    }

    setIsClockedOut(true)
    setClockOutTime(timestamp)

    setAllowed({
      clockin: true,
      breakstart: false,
      breakend: false,
      clockout: false,
    })
    await refreshInternStatus()

    await completeAttendanceAction("Intern Clocked Out Successfully")
  }

  const handleClockOut = async () => {
    try {
      await startFaceVerificationAndAction(executeClockOut)
    } catch (err) {
      console.error(err)
      alert("Failed to clock out.")
    }
  }

  useEffect(() => {
    const orgId = item?.organization_id || org?.organization_id
    if (!orgId) return
    if (isKioskMode) return

    const organizationPersonId = item?.organization_person_id || item?.intern_id || organizationPersonIdFromContext;
    if (organizationPersonId) {
      // Cancel any previous fetch
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort()
      }
      
      const abortController = new AbortController()
      fetchAbortControllerRef.current = abortController
      
      const token = localStorage.getItem("token")
      if (!token) return
      
      ;(async () => {
        try {
          const intern = await fetchFilterInternStatus({
            MAIN_URL,
            organizationId: orgId,
            token,
            organizationPersonId: organizationPersonId,
            internId: item?.intern_id || organizationPersonIdFromContext,
          })

          // Check if fetch was aborted
          if (abortController.signal.aborted) return

          if (intern) {
            // The API returns 'id' field which IS the organization_person_id
            // Verify the intern matches the organization_person_id we're looking for
            const internOrgPersonId = intern.id || intern.organization_person_id;
            const targetId = String(organizationPersonId);
            const internIdStr = String(internOrgPersonId || '');
            const itemOrgPersonId = String(item?.organization_person_id || '');
            
            console.log("[FaceAuth2] Intern data received:", {
              targetId,
              internIdStr,
              itemOrgPersonId,
              internName: intern.Name || intern.name,
              internId: intern.id,
              internCode: intern.Intern_Code
            });
            
            // Check if intern.id (organization_person_id) matches the target
            const idMatches = internIdStr === targetId || 
                             internIdStr === itemOrgPersonId ||
                             (itemOrgPersonId && itemOrgPersonId === targetId && internIdStr === itemOrgPersonId);
            
            // If we have a specific organization_person_id from login, verify it matches
            if (item?.organization_person_id && !idMatches) {
              console.warn("[FaceAuth2] Intern ID mismatch - rejecting:", {
                expected: targetId,
                got: internIdStr,
                itemOrgPersonId: itemOrgPersonId,
                intern: intern.Name || intern.name,
                internId: intern.id
              });
              return; // Don't use this intern if ID doesn't match
            }
            
            if (idMatches) {
              console.log("[FaceAuth2] Intern ID matches - using intern:", {
                targetId,
                internIdStr,
                internName: intern.Name || intern.name
              });
            }

            const internName = intern.Name || intern.name || "Unknown Intern"
            const internFunctionalRole = intern.Functional_Role || intern.functionalRole || "Intern"
            const profileImageUrl =
              intern.profile_image_url || intern.Profile_Image_Url || intern.profileImage || ""
            // The 'id' field IS the organization_person_id in the API response
            const finalOrganizationPersonId = intern.id || intern.organization_person_id || item?.organization_person_id || organizationPersonIdFromContext;
            const finalInternId = intern.intern_id || intern.Intern_ID || item?.intern_id || null;

            const currentTotal = intern?.Total_Worked_Time
            const TotalAttendance = intern?.Today_Attendance
            const TotalBreaks = intern?.Total_Breaks
            const Should_Stop_BreakCal = intern?.Should_Stop_BreakCal
            const Stop_Cal_Work = intern?.Stop_Cal_Work

            setUserData2({
              name: internName,
              functionalRole: internFunctionalRole,
              authorized: false,
              organization_person_id: finalOrganizationPersonId,
              intern_id: finalInternId, // Keep for backward compatibility
              profileImage: profileImageUrl,
              currentTotal: currentTotal,
              TotalAttendance: TotalAttendance,
              TotalBreaks: TotalBreaks,
              Should_Stop_BreakCal: Should_Stop_BreakCal,
              Stop_Cal_Work: Stop_Cal_Work,
            })
            setHasPreselectedIntern(true)
            setShowResults(false)

            setSearchQuery(internName)

            setAllowed({
              clockin: Boolean(intern.clockin ?? true),
              breakstart: Boolean(intern.breakstart ?? false),
              breakend: Boolean(intern.breakend ?? false),
              clockout: Boolean(intern.clockout ?? false),
            })
          }
        } catch (e) {
          if (e.name === 'AbortError') {
            console.log("[v0] Fetch aborted")
            return
          }
          console.log("[v0] auto-fetch intern failed:", e)
        }
      })()
      
      // Cleanup function
      return () => {
        abortController.abort()
      }
    }
  }, [organizationPersonIdFromContext, item?.organization_id, item?.organization_person_id, item?.intern_id, org?.organization_id, isKioskMode])

  useEffect(() => {
    if (isKioskMode) {
      setHasPreselectedIntern(false)
      return
    }
    if (item?.organization_person_id || item?.intern_id) {
      setHasPreselectedIntern(true)
    }
  }, [item?.organization_person_id, item?.intern_id, isKioskMode])

  useEffect(() => {
    if (item?.organization_id && isKioskMode) {
      persistAttendanceLandingState(item)
    }
  }, [item, isKioskMode])

  useEffect(() => {
    if (org?.organization_id) {
      setOrganizationData({
        organization_id: item?.organization_id,
        name: item?.organization_name || "Loading...",
      })
      setApiUrl(`${MAIN_URL}/api/organizations/${item?.organization_id}/filter-intern`)
    }
  }, [org])

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date()
      const date = now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      const time = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      setCurrentDateTime(`${date} | ${time}`)
    }

    updateDateTime()
    const interval = setInterval(updateDateTime, 1000)

    return () => clearInterval(interval)
  }, [])

  console.log("users data 2", userData2)

  const isKioskAwaitingScan = isKioskMode && userData2?.name === "Name"
  const isKioskPersonMatched = isKioskMode && userData2?.name !== "Name"

  return (
    <StyledContainer
      sx={{
        ...(isKioskMode && {
          height: "100%",
          minHeight: 0,
          maxHeight: "100%",
          overflow: isKioskPersonMatched ? "auto" : "hidden",
          padding: {
            xs: "52px 12px 12px",
            sm: "56px 16px 12px",
          },
        }),
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 1300,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          "@media (max-width:600px)": {
            position: "static",
            justifyContent: "center",
            mt: 1,
          },
        }}
      >
        {showChangeOrganization && (
          <Button
            variant="contained"
            color="error"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "8px",
              padding: "6px 16px",
              backgroundColor: "blue",
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "#2f71d3ff",
                boxShadow: 3,
              },
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
            }}
          >
            Change Organization
          </Button>
        )}

        {isKioskMode && userData2?.name !== "Name" && (
          <Button
            variant="contained"
            onClick={resetForNextIntern}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "8px",
              padding: "6px 16px",
              backgroundColor: "#4caf50",
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "#45a049",
                boxShadow: 3,
              },
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
            }}
          >
            Next Intern
          </Button>
        )}

        <Button
          variant="contained"
          color="error"
          startIcon={<LogOutIcon />}
          onClick={handleLogout}
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            borderRadius: "8px",
            padding: "6px 16px",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "#d32f2f",
              boxShadow: 3,
            },
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
          }}
        >
          Logout
        </Button>
      </Stack>

      {showToast && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#ffffff",
            color: "text.primary",
            padding: "12px 24px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 9999,
            fontSize: "16px",
            fontWeight: "500",
            animation: "slideIn 0.3s ease-out",
          }}
        >
          {toastMessage}
        </div>
      )}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      <Box
        sx={{
          width: "100%",
          maxWidth: 460,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          ...(isKioskMode && {
            flex: 1,
            justifyContent: "center",
            minHeight: 0,
          }),
        }}
      >
      {!isKioskAwaitingScan && (
      <HeaderSection sx={{ marginBottom: isKioskMode ? 3 : 1.75, flexShrink: 0 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            fontSize: isKioskMode ? "16px" : "14px",
            marginBottom: isKioskMode ? 1 : 0.5,
          }}
        >
          HR ERP
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: isKioskMode ? "14px" : "13px",
            color: "text.secondary",
            marginBottom: isKioskMode ? 2 : 0.75,
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: isKioskMode
                ? { xs: "1.15rem", sm: "1.35rem" }
                : { xs: "1rem", sm: "1.15rem" },
              color: "text.primary",
            }}
          >
            {item?.organization_name}
          </Box>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: "bold",
            fontSize: isKioskMode ? "14px" : "13px",
            color: "text.primary",
            mb: isKioskMode ? 3 : 1.25,
          }}
        >
          {currentDateTime}
        </Typography>

        {userData2?.name !== "Name" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.75, sm: 1 },
              flexWrap: "nowrap",
              width: "100%",
            }}
          >
            <WorkTimeClock
              compact={!isKioskMode}
              stop={userData2?.Stop_Cal_Work == true || userData2?.Should_Stop_BreakCal == false}
              heading="Total Work Time"
              currentTotal={userData2?.currentTotal}
            />

            <WorkTimeClock
              compact={!isKioskMode}
              stop={userData2?.Should_Stop_BreakCal}
              heading="Total Break Time"
              currentTotal={userData2?.TotalBreaks}
            />
          </Box>
        )}
      </HeaderSection>
      )}

      {(!isKioskMode &&
        !hasPreselectedIntern &&
        !item?.organization_person_id &&
        !item?.intern_id) && (
        <SearchContainer className="search-container">
          <StyledTextField
            fullWidth
            variant="outlined"
            placeholder={
              isKioskMode
                ? "Search intern by code or name..."
                : "Search by intern code..."
            }
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "#4caf50", marginRight: 1 }} />,
              endAdornment: isSearching && <CircularProgress size={20} sx={{ color: "#4caf50" }} />,
            }}
            size="small"
          />

          {showResults && searchResults?.length > 0 && (
            <SearchResultsContainer elevation={3}>
              <List dense>
                {searchResults?.map((intern, index) => (
                  <SearchResultItem
                    key={intern.id || intern.intern_id || index}
                    onClick={() => handleInternSelect(intern)}
                  >
                    <ListItemText
                      primary={`${intern.Name || intern.name || "Unknown Intern"} (${intern.Intern_Code || intern.intern_code || "No Code"})`}
                      secondary={intern.Functional_Role || intern.functionalRole || "Intern"}
                      primaryTypographyProps={{ fontSize: "14px" }}
                      secondaryTypographyProps={{
                        fontSize: "12px",
                        color: "text.primary",
                      }}
                    />
                  </SearchResultItem>
                ))}
              </List>
            </SearchResultsContainer>
          )}

          {showResults && searchResults.length === 0 && !isSearching && searchQuery.trim() && (
            <SearchResultsContainer elevation={3}>
              <Box sx={{ padding: 2, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "text.primary", fontSize: "12px" }}>
                  {'No interns found matching "'}
                  {searchQuery}
                  {'"'}
                </Typography>
              </Box>
            </SearchResultsContainer>
          )}
        </SearchContainer>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          flexShrink: 0,
        }}
      >
        <CircleContainer
          onClick={handleCameraClick}
          sx={{
            cursor:
              isKioskMode && userData2?.name === "Name"
                ? "pointer"
                : !isKioskMode && !userData2?.authorized
                  ? "pointer"
                  : "default",
            width: isKioskAwaitingScan
              ? { xs: 230, sm: 270 }
              : isKioskPersonMatched
                ? { xs: 160, sm: 180 }
                : isKioskMode
                  ? 200
                  : { xs: 145, sm: 160 },
            height: isKioskAwaitingScan
              ? { xs: 230, sm: 270 }
              : isKioskPersonMatched
                ? { xs: 160, sm: 180 }
                : isKioskMode
                  ? 200
                  : { xs: 145, sm: 160 },
            margin: isKioskAwaitingScan
              ? { xs: "12px 0", sm: "16px 0" }
              : isKioskPersonMatched
                ? { xs: "12px 0 8px", sm: "16px 0 12px" }
                : isKioskMode
                  ? undefined
                  : { xs: "6px 0", sm: "8px 0" },
            flexShrink: 0,
            borderWidth: isKioskAwaitingScan ? "3px" : "2px",
          }}
        >
          {isCameraActive ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          ) : isKioskMode && (isLoading || isCapturing) ? (
            <Box sx={{ textAlign: "center", px: 1 }}>
              <CircularProgress size={isKioskAwaitingScan ? 42 : 32} sx={{ color: "#4caf50" }} />
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                Scanning face...
              </Typography>
            </Box>
          ) : !isKioskMode && (faceCaptureStatus === "verifying" || isLoading) ? (
            <CircularProgress size={32} sx={{ color: "#4caf50" }} />
          ) : userData2?.profileImage &&
          userData2.profileImage !== "Profile_Image_Url" ? (
            <img
              src={userData2.profileImage || "/placeholder.svg"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          ) : (
            <>
              <CameraAltIcon
                sx={{
                  fontSize: isKioskAwaitingScan ? 78 : 60,
                  color: "#4caf50",
                  marginBottom: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: isKioskAwaitingScan ? "12px" : "10px",
                  color: "text.secondary",
                  textAlign: "center",
                  lineHeight: 1.2,
                  fontWeight: isKioskAwaitingScan ? 600 : 400,
                }}
              >
                {isKioskMode ? "Tap to scan face" : "Face recognition"}
              </Typography>
            </>
          )}
        </CircleContainer>

        {!isKioskMode &&
          faceCaptureStatus &&
          faceCaptureStatus !== "failed" && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              fontSize: "10px",
              fontWeight: 600,
              lineHeight: 1.2,
              color:
                faceCaptureStatus === "verified"
                  ? "#2e7d32"
                  : "text.secondary",
            }}
          >
            {faceCaptureStatus === "opening" && "Opening camera..."}
            {faceCaptureStatus === "capturing" && "Look at the camera"}
            {faceCaptureStatus === "verifying" && "Verifying face..."}
            {faceCaptureStatus === "verified" && "Face verified"}
          </Typography>
        )}

        {!isKioskMode && capturedImage && !userData2?.profileImage && (
          <ChangePhotoButton onClick={handleChangePhoto}>Change Photo</ChangePhotoButton>
        )}
      </Box>

      {cameraError && (
        <Box
          sx={{
            textAlign: "center",
            width: "100%",
            maxWidth: 280,
            mx: "auto",
            alignSelf: "center",
            mb: 0.5,
            mt: 0.25,
            px: 1,
            py: 0.35,
            backgroundColor: "#ffebee",
            borderRadius: "6px",
            border: "1px solid #ffcdd2",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#d32f2f",
              fontSize: { xs: "9.5px", sm: "10.5px" },
              lineHeight: 1.2,
              fontWeight: 500,
            }}
          >
            {cameraError}
          </Typography>
        </Box>
      )}

      {!isKioskAwaitingScan && (
        <>
      <Box sx={{ textAlign: "center", marginBottom: isKioskMode ? 3 : 1.75, flexShrink: 0 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "text.primary",
            marginBottom: 0.35,
            fontSize: isKioskMode ? undefined : { xs: "1rem", sm: "1.05rem" },
          }}
        >
          {userData2.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: isKioskMode ? undefined : { xs: "0.8rem", sm: "0.85rem" },
          }}
        >
          {userData2.functionalRole}
        </Typography>
      </Box>

      <ButtonContainer sx={{ marginTop: isKioskMode ? undefined : 1.25, flexShrink: 0, width: "100%", justifyContent: "center" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "58px",
              minHeight: "20px",
              backgroundColor: "#111827",
              color: "#ffffff !important",
              borderRadius: "999px",
              px: 1,
              py: 0.25,
              lineHeight: 1.15,
              fontWeight: 700,
              letterSpacing: "0.3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.28)",
              visibility: clockInDisplayTime ? "visible" : "hidden",
            }}
          >
            {clockInDisplayTime || "--:--"}
          </Typography>
          <ActionButton
            variant="outlined"
            onClick={handleClockIn}
            disabled={!allowed.clockin || buttonsDisabled || isLoading || isCameraActive}
            sx={{
              opacity: !allowed.clockin ? 0.7 : 1,
              backgroundColor: !allowed.clockin ? theme.palette.background.default : "#ffffff",
              color: !allowed.clockin ? "#2e7d32" : "#333",
              cursor: !allowed.clockin ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: !allowed.clockin ? theme.palette.background.default : "#f0f8f0",
              },
            }}
          >
            Clock
            <br />
            In
          </ActionButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "58px",
              minHeight: "20px",
              backgroundColor: "#111827",
              color: "#ffffff !important",
              borderRadius: "999px",
              px: 1,
              py: 0.25,
              lineHeight: 1.15,
              fontWeight: 700,
              letterSpacing: "0.3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.28)",
              visibility: breakStartDisplayTime ? "visible" : "hidden",
            }}
          >
            {breakStartDisplayTime || "--:--"}
          </Typography>
          <ActionButton
            variant="outlined"
            onClick={handleStartBreak}
            disabled={!allowed.breakstart || buttonsDisabled || isLoading || isCameraActive}
            sx={{
              opacity: !allowed.breakstart ? 0.7 : 1,
              backgroundColor: !allowed.breakstart ? theme.palette.background.default : "white",
              color: !allowed.breakstart ? "#2e7d32" : "#333",
              cursor: !allowed.breakstart ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: !allowed.breakstart ? "#e8f5e8" : "#f0f8f0",
              },
            }}
          >
            Break
            <br />
            Start
          </ActionButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "58px",
              minHeight: "20px",
              backgroundColor: "#111827",
              color: "#ffffff !important",
              borderRadius: "999px",
              px: 1,
              py: 0.25,
              lineHeight: 1.15,
              fontWeight: 700,
              letterSpacing: "0.3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.28)",
              visibility: breakEndDisplayTime ? "visible" : "hidden",
            }}
          >
            {breakEndDisplayTime || "--:--"}
          </Typography>
          <ActionButton
            variant="outlined"
            onClick={handleEndBreak}
            disabled={!allowed.breakend || buttonsDisabled || isLoading || isCameraActive}
            sx={{
              opacity: !allowed.breakend ? 0.7 : 1,
              backgroundColor: !allowed.breakend ? theme.palette.background.default : "white",
              color: !allowed.breakend ? "#2e7d32" : "#333",
              cursor: !allowed.breakend ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: !allowed.breakend ? "#e8f5e8" : "#f0f8f0",
              },
            }}
          >
            Break
            <br />
            End
          </ActionButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "58px",
              minHeight: "20px",
              backgroundColor: "#111827",
              color: "#ffffff !important",
              borderRadius: "999px",
              px: 1,
              py: 0.25,
              lineHeight: 1.15,
              fontWeight: 700,
              letterSpacing: "0.3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.28)",
              visibility: clockOutDisplayTime ? "visible" : "hidden",
            }}
          >
            {clockOutDisplayTime || "--:--"}
          </Typography>
          <ActionButton
            variant="outlined"
            onClick={handleClockOut}
            disabled={!allowed.clockout || buttonsDisabled || isLoading || isCameraActive}
            sx={{
              opacity: !allowed.clockout ? 0.7 : 1,
              backgroundColor: !allowed.clockout ? theme.palette.background.default : "white",
              color: !allowed.clockout ? "#2e7d32" : "#333",
              cursor: !allowed.clockout ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: !allowed.clockout ? "#e8f5e8" : "#f0f8f0",
              },
            }}
          >
            Clock
            <br />
            Out
          </ActionButton>
        </Box>
      </ButtonContainer>
        </>
      )}

      </Box>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </StyledContainer>
  )
}

async function fetchFilterInternStatus(opts) {
  try {
    const { MAIN_URL, organizationId, token, organizationPersonId, internId, internCode, name } = opts
    if (!organizationId) throw new Error("Missing organization id")

    const url = new URL(`${MAIN_URL}/api/organizations/${organizationId}/filter-intern`)
    // Prioritize ID-based searches - only use name if no IDs are provided
    if (organizationPersonId) {
      url.searchParams.set("organization_person_id", String(organizationPersonId))
    } else if (internId) {
      url.searchParams.set("intern_id", String(internId))
    } else if (internCode) {
      url.searchParams.set("intern_code", String(internCode))
    } else if (name) {
      // Only use name as last resort
      url.searchParams.set("q", String(name))
    }

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })

    if (!res.ok) {
      const text = await res.text().catch(() => "")
      throw new Error(`filter-intern failed: ${res.status} ${text}`)
    }

    const data = await res.json()

    let list = []
    if (Array.isArray(data)) {
      list = data
    } else if (Array.isArray(data?.data)) {
      list = data.data
    } else if (Array.isArray(data?.interns)) {
      list = data.interns
    } else if (Array.isArray(data?.[0])) {
      list = data[0]
    } else if (data) {
      list = [data]
    }

    // If we have an organizationPersonId or internId, try to find the exact match in the list
    // Note: The API returns 'id' field which IS the organization_person_id
    const searchId = organizationPersonId || internId;
    if (searchId && Array.isArray(list)) {
      const targetId = String(searchId);
      const match = list.find((e) => {
        // The 'id' field in API response IS the organization_person_id
        // Priority: id (organization_person_id) > organization_person_id > intern_id
        const internOrgPersonId = e?.id || e?.organization_person_id;
        const internId = e?.intern_id || e?.Intern_ID;
        
        // Check if id (organization_person_id) matches
        if (internOrgPersonId && String(internOrgPersonId) === targetId) {
          return true;
        }
        
        // Also check intern_id as fallback
        if (internId && String(internId) === targetId) {
          return true;
        }
        
        return false;
      });
      
      if (match) {
        console.log("[fetchFilterInternStatus] Found exact match:", {
          targetId,
          matchedId: match.id || match.organization_person_id,
          internName: match?.Name || match?.name,
          internCode: match?.Intern_Code
        });
        return match;
      }
      
      // If no exact match found, log warning but return first item as fallback
      console.warn("[fetchFilterInternStatus] No exact match found for organization_person_id:", targetId, "Available IDs:", list.map(e => ({ id: e.id, name: e.Name || e.name })));
      return Array.isArray(list) && list.length > 0 ? list[0] : null;
    }

    // If no search ID provided, return first item (for search functionality)
    return Array.isArray(list) ? list[0] : list
  } catch (err) {
    if (err.message && err.message.includes("401")) {
      toast.error("Session Expired!")
      window.location.href = "/login"
      return
    }
    if (err.response && err.response.status === 401) {
      toast.error("Session Expired!")
      window.location.href = "/login"
      return
    }
    console.error("Error in fetchFilterInternStatus:", err)
    toast.error(err.message || "Unexpected error occurred")
  }
}

export default FaceAuth2

















